/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { GoBackButton, NavigationMenu, SideMenuLayout } from '@veeam/components';

import type { CSSProperties } from 'react';
import type { NavigationMenuProps, NavigationGroup, NavigationItem } from '@veeam/components';

import { backIconSrc } from '../../icons';
import { capitalize } from '../../helpers/string-helpers';
import {
    useGlobalAppData,
    useGlobalContext,
    useGlobalLang,
    useGlobalServices,
} from '../providers/GlobalContextProvider';
import { NotificationDialogsTextKeys } from '../../services/notification';

import type { SideMenuGroup, SideMenuItem } from '../../stores/router';

export interface PortalSideMenuProps {
    groups: SideMenuGroup[];
    items: SideMenuItem[];
    children: React.ReactElement;
    renderHeader?: NavigationMenuProps['renderHeader'];
    sideMenuStyle?: CSSProperties;
    navigationMenuStyle?: CSSProperties;
    keepQuery?: boolean;
    hideBorder?: boolean;
}

export const PortalSideMenu: React.FC<PortalSideMenuProps> = observer((props) => {
    const {
        groups = [],
        items = [],
        children,
        renderHeader,
        sideMenuStyle,
        navigationMenuStyle,
        keepQuery = true,
        hideBorder = false,
    } = props;
    const { notificationService } = useGlobalServices();
    const globalAppData = useGlobalAppData();
    const { navMenuWidth, exitConfigIconSrc, exitButtonTextKey } = globalAppData;
    const { getLoginPath } = useGlobalContext();
    const lang = useGlobalLang();
    const navigate = useNavigate();
    const location = useLocation();
    const navGroups: NavigationGroup[] = groups.map(({ groupId, title, titleKey }) => ({
        id: groupId,
        title: title ?? capitalize(lang[titleKey]),
    }));
    const navItems: NavigationItem[] = items.map(({ exact, to, groupId, iconSrc, text, textKey, onClick }) => {
        const locationPathArr = location.pathname.split('/');
        const toPathArr = to.split('/');
        const isIncludeToActive = toPathArr.every((toPath, index) => toPath === locationPathArr[index]);

        return {
            groupId,
            icon: iconSrc,
            title: text ?? capitalize(lang[textKey]),
            active: exact ? to === location.pathname : isIncludeToActive,
            onClick: onClick ?? ((event) => {
                event.preventDefault();

                if (globalAppData.navigationBlockerPrompt) {
                    return notificationService.confirm(globalAppData.navigationBlockerPrompt.title, globalAppData.navigationBlockerPrompt.message)
                        .then((btn) => {
                            if (btn === NotificationDialogsTextKeys.Yes) {
                                globalAppData.navigationBlockerPrompt = null;
                                navigate(`${to}${keepQuery ? window.location.search : ''}`);
                            }
                        });
                }

                navigate(`${to}${keepQuery ? window.location.search : ''}`);
            }),
        };
    });
    const triangleColor: string = items[navItems.findIndex(item => item.active)]?.triangleColor;

    return (
        <SideMenuLayout
            style={sideMenuStyle}
            menu={(
                <NavigationMenu
                    renderHeader={(...args) => (
                        exitButtonTextKey
                            ? (
                                <GoBackButton
                                    text={capitalize(lang[exitButtonTextKey]) ?? 'Go Back'}
                                    icon={exitConfigIconSrc ?? backIconSrc}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        window.location.assign(getLoginPath());
                                    }}
                                />
                            )
                            : renderHeader?.(...args)
                    )}
                    triangleColor={triangleColor}
                    groups={navGroups.some(({ title }) => Boolean(title)) ? navGroups : undefined}
                    items={navItems}
                    width={navMenuWidth}
                    style={navigationMenuStyle}
                    hideBorder={hideBorder}
                />
            )}
            body={children}
        />
    );
});
